import { message, Modal, Select } from "antd";
import axios from "axios";
import _ from "lodash";
import prettierPluginHtml from "prettier/plugins/html";
import prettierPluginCSS from "prettier/plugins/postcss";
import prettier from "prettier/standalone";
import * as R from "ramda";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useAsyncFn } from "react-use";
import useSWR from "swr";
import Buttons from "../../components/aahk/configManager/components/Buttons";
import ConfigEditer from "../../components/aahk/configManager/components/ConfigEditer";
import Previewer from "../../components/aahk/configManager/components/Previewer";
import useDraft from "../../components/aahk/configManager/hooks/useDraft";
import { ConfigVariableList } from "../../components/aahk/configManager/types";
import ErrorBoundary from "../../components/wrappers/ErrorBoundary";
import Loading from "../../components/wrappers/Loading";
import { RequestError } from "../../types/Error";
import { fetcher } from "../../util/request";
import { EmailTemplate } from "../../types/admin/EmailTemplate";
import { Button, Icon } from "semantic-ui-react";

const configVariableList: ConfigVariableList<keyof EmailTemplate> = [
  { type: "html", key: "html", label: "Email", editable: true },
  { type: "text", key: "subject", label: "Subject", editable: true },
];

const prettify = (str: string) =>
  prettier.format(str, {
    parser: "html",
    plugins: [prettierPluginHtml, prettierPluginCSS],
  });

const prettifyEmailTemplates = async (
  emailsPromise: Promise<EmailTemplate[]>
) => {
  const emails = await emailsPromise;
  const prettifiedHtmls = await Promise.all(
    emails.map((email) => prettify(email.html))
  );
  return _.zip(emails, prettifiedHtmls).map(
    ([originalEmail, prettifiedHtml]) => ({
      ...originalEmail,
      html: prettifiedHtml,
    })
  ) as EmailTemplate[];
};

// TODO: add docstring
export const EmailPreviewer: React.FC<{ values?: EmailTemplate }> = ({
  values: email,
}) => {
  if (email === undefined) {
    return null;
  }

  return (
    <div className="d-flex flex-column align-items-center w-100">
      <h4>Subject: Access Abroad Hong Kong -- {email.subject}</h4>
      <div className="d-block">
        <div dangerouslySetInnerHTML={{ __html: email.html }}></div>
      </div>
    </div>
  );
};

const EmailTemplates = () => {
  const {
    data: emailsUnsorted,
    error,
    isLoading,
  } = useSWR<EmailTemplate[], RequestError>(
    "/admin/email-template/list",
    R.compose(prettifyEmailTemplates, fetcher)
  );
  const emails = _.sortBy(emailsUnsorted, "id");

  const [emailId, setEmailId] = useState("");
  const email = _.find(emails, { id: emailId });
  const { formMethods, draft: draftEmail } = useDraft(email);

  const navigate = useNavigate();

  const [{ loading: submitting }, onEdit] = useAsyncFn(
    async (draft: EmailTemplate) => {
      const { id, ...editedEmail } = draft;
      return axios
        .patch(`/admin/email-template/${id}`, editedEmail)
        .then(() => {
          navigate("");
          message.success("Email template has been edited.");
        })
        .catch(() =>
          message.error("Email template cannot be edited. Please try again.")
        );
    }
  );

  const helpMessage = (
    <>
      <div className="alert alert-warning" role="alert">
        <strong>ALWAYS</strong> ensure your HTML is correctly formatted and
        every tag is properly closed to avoid rendering issues.
      </div>
      <p>
        The email template should be written in HTML. New lines should be marked
        with <code>&lt;br/&gt;</code>.
      </p>
      <p>
        Whitespaces are ignored. Customisable fields are enclosed with the
        syntax <code>{"{| key |}"}</code>, while fields tied to the application
        context should be enclosed with <code>{"{{ key }}"}</code>.
      </p>
      <p>
        The <code>key</code> should consist of English alphabet characters,
        numbers, and underscores only.
      </p>
      <p>Example:</p>
      <pre style={{ whiteSpace: "pre-wrap" }}>
        <code>
          {`Dear {{applicantPreferredName}},<br />
        <br />
        Please <b>select a date</b> from {|startDate|} to {|endDate|} to perform your aptitude test.<br />
        Please reply <u>before {|deadline|}</u>. <br /><br />
        Kind regards,<br />
        Access Abroad Hong Kong<br />`}
        </code>
      </pre>
      <p>
        In this example:
        <ul>
          <li>
            <code>{"{{applicantPreferredName}}"}</code> is a field tied to the
            application context, which will be replaced with the applicant's
            preferred name.
          </li>
          <li>
            <code>{"{|startDate|}"}</code> and <code>{"{|endDate|}"}</code> are
            customizable fields where the user can input the start and end dates
            for the aptitude test.
          </li>
          <li>
            <code>{"{|deadline|}"}</code> is another customizable field where
            the user can input the deadline for the reply.
          </li>
        </ul>
      </p>
      <p>
        In addition to the basic fields, you can use common HTML formatting
        tags. Remember to always close your tags properly to maintain valid HTML
        syntax:
      </p>
      <ul>
        <li>
          <b>Bold</b>: wrap your text with the <code>&lt;b&gt;</code> and{" "}
          <code>&lt;/b&gt;</code> tags.
        </li>
        <li>
          <u>Underline</u>: wrap your text with the <code>&lt;u&gt;</code> and{" "}
          <code>&lt;/u&gt;</code> tags.
        </li>
        <li>
          <em>Italic</em>: wrap your text with the <code>&lt;em&gt;</code> and{" "}
          <code>&lt;/em&gt;</code> tags.
        </li>
      </ul>
      <p>To create lists, use the following:</p>
      <ol>
        <li>
          For ordered (numbered) lists, wrap each list item with the{" "}
          <code>&lt;li&gt;</code> and <code>&lt;/li&gt;</code> tags inside the{" "}
          <code>&lt;ol&gt;</code> and <code>&lt;/ol&gt;</code> tags.
        </li>
      </ol>
      <ul>
        <li>
          For unordered (bulleted) lists, wrap each list item with the{" "}
          <code>&lt;li&gt;</code> and <code>&lt;/li&gt;</code> tags inside the{" "}
          <code>&lt;ul&gt;</code> and <code>&lt;/ul&gt;</code> tags.
        </li>
      </ul>
    </>
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEmailId, setNewEmailId] = useState("");
  const [newEmailSubject, setNewEmailSubject] = useState("");
  const [validationError, setValidationError] = useState("");

  const validateAndCreateTemplate = async () => {
    // Reset validation error
    setValidationError("");

    // Validate inputs
    if (!newEmailId.trim()) {
      setValidationError("Email ID is required");
      return;
    }

    if (!newEmailSubject.trim()) {
      setValidationError("Subject is required");
      return;
    }

    try {
      // Send PUT request to backend
      await axios.put("/admin/email-template", {
        id: newEmailId.trim(),
        subject: newEmailSubject.trim(),
        html: "", // Empty email content
      });

      // Close modal and show success message
      setIsModalVisible(false);
      message.success(
        "Email template created successfully. Refresh the page to view the new template."
      );
    } catch (error) {
      message.error("Failed to create email template");
    }
  };

  const handleDeleteTemplate = () => {
    if (!emailId) return;

    Modal.confirm({
      title: "Delete Email Template",
      content: (
        <div>
          <p>Are you sure you want to delete this email template?</p>
          <p>
            <strong>Template ID:</strong> {emailId}
          </p>
          <div className="alert alert-danger mt-3">
            <strong>Warning:</strong> This action cannot be undone. All emails
            using this template will no longer be able to be sent.
          </div>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axios.delete(`/admin/email-template/${emailId}`);
          message.success(
            "Email template deleted successfully. Reload the page to see changes."
          );

          // Reset selected email and update the list
          setEmailId("");
        } catch (error) {
          message.error("Failed to delete email template");
          console.error("Delete error:", error);
        }
      },
    });
  };

  return (
    <Loading loading={false} error={error}>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <Select
            loading={isLoading}
            options={(emails ?? []).map(({ id }) => ({ value: id, label: id }))}
            value={emailId || undefined}
            onChange={(id) => {
              setEmailId(id);
              navigate("");
            }}
            placeholder="Select an email template"
            popupMatchSelectWidth={false}
            style={{
              minWidth: 300,
              width: "fit-content",
              maxWidth: "100%",
              margin: "16px 1rem",
            }}
          />
          <Buttons
            {...{
              loading: isLoading,
              id: emailId,
              values: email,
              formMethods,
              onSubmitEdit: formMethods.handleSubmit(onEdit),
              submitting: submitting,
              editConfirmModalContent: (
                <p>This template will be used for all future emails.</p>
              ),
            }}
          />
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            onClick={handleDeleteTemplate}
            color="red"
            disabled={!emailId}
          >
            <Icon name="trash alternate" />
            Delete
          </Button>

          <Button
            onClick={() => {
              // Reset form fields
              setNewEmailId("");
              setNewEmailSubject("Access Abroad Hong Kong -- ");
              setValidationError("");
              // Show modal
              setIsModalVisible(true);
            }}
            color="green"
          >
            <Icon name="plus" />
            New
          </Button>
          <Button
            onClick={() =>
              Modal.info({
                title: "Help",
                content: (
                  <div
                    className="pe-2"
                    style={{ maxHeight: "75vh", overflowY: "auto" }}
                  >
                    {helpMessage}
                  </div>
                ),
                width: 800,
                style: { top: "3vh" },
              })
            }
            color="yellow"
          >
            <Icon name="question circle" />
            Help
          </Button>
        </div>
      </div>
      <FormProvider {...formMethods}>
        <ErrorBoundary>
          <Routes>
            <Route
              path="*"
              element={
                <ConfigEditer editing={false} {...{ configVariableList }} />
              }
            />
            <Route
              path=":emailId/*"
              element={
                <>
                  <Routes>
                    <Route
                      path="preview"
                      element={
                        <Previewer
                          editing={false}
                          values={email}
                          component={EmailPreviewer}
                        />
                      }
                    />
                    <Route path="edit">
                      <Route
                        path=""
                        element={
                          <ConfigEditer editing {...{ configVariableList }} />
                        }
                      />
                      <Route
                        path="preview"
                        element={
                          <Previewer
                            editing
                            values={draftEmail ?? email}
                            component={EmailPreviewer}
                          />
                        }
                      />
                    </Route>
                  </Routes>
                </>
              }
            />
          </Routes>
        </ErrorBoundary>
      </FormProvider>

      {/* Create Email Template Modal */}
      <Modal
        title="Create new email template"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={validateAndCreateTemplate}
        okText="Create"
      >
        <div className="mb-3">
          <label className="form-label">Email ID:</label>
          <input
            type="text"
            className="form-control"
            value={newEmailId}
            onChange={(e) => setNewEmailId(e.target.value)}
            placeholder="Enter email template ID"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Email Subject:</label>
          <input
            type="text"
            className="form-control"
            value={newEmailSubject}
            onChange={(e) => setNewEmailSubject(e.target.value)}
            placeholder="Enter email subject"
          />
        </div>

        {validationError && (
          <div className="text-danger mt-2">{validationError}</div>
        )}
      </Modal>
    </Loading>
  );
};

export default EmailTemplates;
